import type { DeepFieldGraphRole } from './types';

export const DEEP_FIELD_GRAPH_ROLES: readonly DeepFieldGraphRole[] = [
    // Tech
    {
        label: 'Software Engineer',
        domain: 'tech',
        skills: ['Python', 'TypeScript', 'React', 'Docker', 'CI/CD', 'GraphQL', 'PostgreSQL', 'Microservices'],
        agents: ['Code review agent', 'Documentation agent', 'Test generation agent'],
    },
    {
        label: 'DevOps Engineer',
        domain: 'tech',
        skills: ['Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'AWS', 'Prometheus', 'Linux Admin', 'Networking'],
        agents: ['Incident triage agent', 'Deployment automation agent', 'Log analysis agent'],
    },
    {
        label: 'Cloud Architect',
        domain: 'tech',
        skills: [
            'Cloud Architecture',
            'AWS',
            'Azure',
            'GCP',
            'Microservices',
            'Load Balancing',
            'Edge Computing',
            'Kafka',
        ],
        agents: ['Cost optimization agent', 'Architecture review agent', 'Security compliance agent'],
    },
    {
        label: 'Backend Developer',
        domain: 'tech',
        skills: ['Python', 'Go', 'Rust', 'PostgreSQL', 'Redis', 'gRPC', 'Kafka', 'MongoDB'],
        agents: ['API design agent', 'Schema migration agent', 'Performance profiling agent'],
    },
    {
        label: 'Frontend Developer',
        domain: 'tech',
        skills: ['TypeScript', 'React', 'GraphQL', 'WebAssembly', 'Figma', 'Accessibility', 'Design Systems'],
        agents: ['Accessibility audit agent', 'Component documentation agent', 'Bundle analysis agent'],
    },
    {
        label: 'Site Reliability Engineer',
        domain: 'tech',
        skills: [
            'Kubernetes',
            'Prometheus',
            'Grafana',
            'Linux Admin',
            'Networking',
            'Docker',
            'Terraform',
            'Load Balancing',
        ],
        agents: ['SLO monitoring agent', 'Runbook automation agent', 'Capacity planning agent'],
    },
    {
        label: 'Security Engineer',
        domain: 'tech',
        skills: ['Cybersecurity', 'Networking', 'Cloud Architecture', 'Data Privacy', 'Linux Admin', 'DNS'],
        agents: ['Vulnerability scanning agent', 'Policy compliance agent', 'Incident response agent'],
    },
    {
        label: 'Mobile Developer',
        domain: 'tech',
        skills: ['Swift', 'Kotlin', 'React', 'TypeScript', 'CI/CD', 'Figma', 'API Design'],
        agents: ['Crash report triage agent', 'Store submission agent', 'Localization agent'],
    },
    {
        label: 'Platform Engineer',
        domain: 'tech',
        skills: ['Kubernetes', 'Docker', 'Terraform', 'AWS', 'CI/CD', 'Microservices', 'Kafka', 'gRPC'],
        agents: ['Developer onboarding agent', 'Resource provisioning agent', 'Service mesh config agent'],
    },
    {
        label: 'Database Administrator',
        domain: 'tech',
        skills: ['PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch', 'SQL', 'Data Warehousing', 'Load Balancing'],
        agents: ['Query optimization agent', 'Backup verification agent', 'Schema drift agent'],
    },
    // Data & AI
    {
        label: 'Data Scientist',
        domain: 'data',
        skills: [
            'Machine Learning',
            'Python',
            'Statistical Modeling',
            'Deep Learning',
            'NLP',
            'A/B Testing',
            'Time Series',
        ],
        agents: ['Experiment tracking agent', 'Feature importance agent', 'Model registry agent'],
    },
    {
        label: 'ML Engineer',
        domain: 'data',
        skills: ['Machine Learning', 'MLOps', 'Deep Learning', 'Feature Engineering', 'Python', 'Spark', 'Docker'],
        agents: ['Training pipeline agent', 'Model deployment agent', 'Data drift detection agent'],
    },
    {
        label: 'AI Researcher',
        domain: 'data',
        skills: [
            'Deep Learning',
            'NLP',
            'Computer Vision',
            'Reinforcement Learning',
            'LLMs',
            'Fine-Tuning',
            'AI Safety',
        ],
        agents: ['Literature synthesis agent', 'Reproducibility checker agent', 'Benchmark tracking agent'],
    },
    {
        label: 'Data Engineer',
        domain: 'data',
        skills: ['ETL Pipelines', 'Spark', 'SQL', 'Data Warehousing', 'Airflow', 'dbt', 'Snowflake', 'BigQuery'],
        agents: ['Pipeline lineage agent', 'Data quality agent', 'Schema evolution agent'],
    },
    {
        label: 'Prompt Engineer',
        domain: 'data',
        skills: ['Prompt Engineering', 'LLMs', 'RAG', 'Embeddings', 'Vector Databases', 'Generative AI', 'Fine-Tuning'],
        agents: ['Prompt evaluation agent', 'RAG retrieval agent', 'Safety guardrail agent'],
    },
    {
        label: 'Data Analyst',
        domain: 'data',
        skills: ['SQL', 'Statistical Modeling', 'A/B Testing', 'BigQuery', 'Data Governance', 'Time Series', 'Python'],
        agents: ['Dashboard annotation agent', 'Anomaly detection agent', 'Report generation agent'],
    },
    {
        label: 'AI Product Manager',
        domain: 'data',
        skills: [
            'LLMs',
            'Generative AI',
            'Product Roadmapping',
            'A/B Testing',
            'Prompt Engineering',
            'Stakeholder Management',
        ],
        agents: ['Use case scoping agent', 'Model card generator agent', 'Ethics review agent'],
    },
    {
        label: 'Computer Vision Engineer',
        domain: 'data',
        skills: ['Computer Vision', 'Deep Learning', 'Python', 'MLOps', 'Feature Engineering', 'Anomaly Detection'],
        agents: ['Annotation QA agent', 'Dataset balancing agent', 'Inference optimization agent'],
    },
    // People & HR
    {
        label: 'HR Business Partner',
        domain: 'people',
        skills: ['HR Strategy', 'Coaching', 'Talent Planning', 'Performance Mgmt', 'Employee Engagement', 'DEI'],
        agents: ['1:1 prep agent', 'ER case summarizer agent', 'Manager coaching agent'],
    },
    {
        label: 'Talent Acquisition Lead',
        domain: 'people',
        skills: ['Recruiting', 'Employer Branding', 'Onboarding', 'HR Analytics', 'HRIS', 'Competency Modeling'],
        agents: ['Resume screening agent', 'Interview scheduling agent', 'Offer letter agent'],
    },
    {
        label: 'VP of People',
        domain: 'people',
        skills: ['HR Strategy', 'Culture Design', 'Succession Planning', 'DEI', 'Workforce Planning', 'L&D'],
        agents: ['Org health insights agent', 'Succession pipeline agent', 'Executive summary agent'],
    },
    {
        label: 'L&D Manager',
        domain: 'people',
        skills: ['L&D', 'Coaching', 'Career Pathing', 'Competency Modeling', 'Team Building', 'Culture Design'],
        agents: ['Learning path agent', 'Skill gap agent', 'Content curation agent'],
    },
    {
        label: 'Compensation Analyst',
        domain: 'people',
        skills: ['Compensation', 'Pay Transparency', 'Benefits Admin', 'HR Analytics', 'Job Architecture', 'HRIS'],
        agents: ['Benchmark matching agent', 'Range calculator agent', 'Equity modeling agent'],
    },
    {
        label: 'People Analytics Lead',
        domain: 'people',
        skills: ['HR Analytics', 'Statistical Modeling', 'Workforce Planning', 'Python', 'HRIS', 'A/B Testing'],
        agents: ['Attrition predictor agent', 'Headcount planning agent', 'D&I metrics agent'],
    },
    {
        label: 'DEIB Director',
        domain: 'people',
        skills: ['DEI', 'Culture Design', 'Employee Engagement', 'HR Strategy', 'Employer Branding', 'Coaching'],
        agents: ['Bias audit agent', 'Program impact agent', 'Inclusion pulse agent'],
    },
    {
        label: 'Org Design Consultant',
        domain: 'people',
        skills: [
            'Job Architecture',
            'Competency Modeling',
            'Career Pathing',
            'Workforce Planning',
            'Change Management',
            'HR Strategy',
        ],
        agents: ['Role mapping agent', 'Span-of-control agent', 'Redesign simulator agent'],
    },
    // Finance & Compliance
    {
        label: 'Compliance Officer',
        domain: 'finance',
        skills: [
            'GDPR',
            'Pay Transparency',
            'SOX Compliance',
            'Regulatory Reporting',
            'Data Privacy',
            'Internal Audit',
        ],
        agents: ['Policy gap agent', 'Regulation tracker agent', 'Audit prep agent'],
    },
    {
        label: 'Financial Analyst',
        domain: 'finance',
        skills: ['Financial Modeling', 'FP&A', 'Budgeting', 'Risk Management', 'IFRS', 'ESG Reporting'],
        agents: ['Forecast variance agent', 'Scenario modeling agent', 'Board pack agent'],
    },
    {
        label: 'Risk Manager',
        domain: 'finance',
        skills: [
            'Risk Management',
            'Fraud Detection',
            'Internal Audit',
            'Contract Management',
            'Regulatory Reporting',
            'GDPR',
        ],
        agents: ['Fraud triage agent', 'Contract extraction agent', 'Risk heatmap agent'],
    },
    {
        label: 'Tax Specialist',
        domain: 'finance',
        skills: ['Tax Planning', 'IFRS', 'Regulatory Reporting', 'Payroll', 'Treasury', 'Contract Management'],
        agents: ['Tax provision agent', 'Filing checklist agent', 'Entity structure agent'],
    },
    {
        label: 'ESG Analyst',
        domain: 'finance',
        skills: [
            'ESG Reporting',
            'Data Privacy',
            'Risk Management',
            'Regulatory Reporting',
            'Budgeting',
            'Statistical Modeling',
        ],
        agents: ['Disclosure mapper agent', 'Carbon footprint agent', 'Sustainability metrics agent'],
    },
    {
        label: 'Internal Auditor',
        domain: 'finance',
        skills: ['Internal Audit', 'SOX Compliance', 'Fraud Detection', 'Risk Management', 'Data Privacy', 'GDPR'],
        agents: ['Control testing agent', 'Finding categorizer agent', 'Remediation tracker agent'],
    },
    // Strategy & Leadership
    {
        label: 'Product Manager',
        domain: 'strategy',
        skills: [
            'Product Roadmapping',
            'Stakeholder Management',
            'Go-to-Market',
            'OKRs',
            'Design Thinking',
            'A/B Testing',
        ],
        agents: ['PRD summarizer agent', 'Competitive intel agent', 'Roadmap prioritizer agent'],
    },
    {
        label: 'Chief Strategy Officer',
        domain: 'strategy',
        skills: [
            'P&L Management',
            'M&A',
            'Board Communication',
            'Market Analysis',
            'Competitive Intelligence',
            'Pricing Strategy',
        ],
        agents: ['Deal memo agent', 'Market sizing agent', 'Board deck agent'],
    },
    {
        label: 'Head of Innovation',
        domain: 'strategy',
        skills: ['Innovation Management', 'Design Thinking', 'Business Development', 'Negotiation', 'Go-to-Market'],
        agents: ['Idea scoring agent', 'Partner scouting agent', 'POC tracker agent'],
    },
    {
        label: 'VP of Engineering',
        domain: 'strategy',
        skills: [
            'Stakeholder Management',
            'OKRs',
            'P&L Management',
            'Agile',
            'Board Communication',
            'Change Management',
        ],
        agents: ['Engineering metrics agent', 'Hiring plan agent', 'Tech debt prioritizer agent'],
    },
    {
        label: 'Chief People Officer',
        domain: 'strategy',
        skills: ['HR Strategy', 'Board Communication', 'Culture Design', 'DEI', 'M&A', 'Succession Planning'],
        agents: ['Comp committee prep agent', 'Integration playbook agent', 'Executive assessment agent'],
    },
    {
        label: 'Business Analyst',
        domain: 'strategy',
        skills: [
            'Market Analysis',
            'Competitive Intelligence',
            'Financial Modeling',
            'Stakeholder Management',
            'Design Thinking',
            'OKRs',
        ],
        agents: ['Requirement analyzer agent', 'Process mapper agent', 'Stakeholder map agent'],
    },
    // Operations
    {
        label: 'Project Manager',
        domain: 'ops',
        skills: ['Project Management', 'Agile', 'Scrum', 'Kanban', 'Stakeholder Management', 'Change Management'],
        agents: ['Status report agent', 'Resource leveling agent', 'Risk log agent'],
    },
    {
        label: 'Operations Manager',
        domain: 'ops',
        skills: ['Supply Chain', 'Process Design', 'Lean', 'Six Sigma', 'Vendor Management', 'Capacity Planning'],
        agents: ['Bottleneck detector agent', 'SLA monitor agent', 'Vendor scorecard agent'],
    },
    {
        label: 'Scrum Master',
        domain: 'ops',
        skills: ['Scrum', 'Agile', 'SAFe', 'Kanban', 'Continuous Improvement', 'Team Building'],
        agents: ['Retro summarizer agent', 'Velocity analyzer agent', 'Blocker escalation agent'],
    },
    {
        label: 'Program Manager',
        domain: 'ops',
        skills: [
            'Project Management',
            'Stakeholder Management',
            'Change Management',
            'OKRs',
            'Vendor Management',
            'Agile',
        ],
        agents: ['Dependency mapper agent', 'Status rollup agent', 'Governance checklist agent'],
    },
    {
        label: 'Quality Engineer',
        domain: 'ops',
        skills: ['Quality Assurance', 'Six Sigma', 'Continuous Improvement', 'Process Design', 'Lean', 'A/B Testing'],
        agents: ['Defect triage agent', 'Test coverage agent', 'Root cause agent'],
    },
    {
        label: 'Supply Chain Director',
        domain: 'ops',
        skills: [
            'Supply Chain',
            'Logistics',
            'Procurement',
            'Vendor Management',
            'Process Design',
            'Capacity Planning',
        ],
        agents: ['Demand forecast agent', 'Inventory optimizer agent', 'Supplier risk agent'],
    },
    // Product & Design
    {
        label: 'UX Designer',
        domain: 'product',
        skills: [
            'User Research',
            'Wireframing',
            'Prototyping',
            'Usability Testing',
            'Figma',
            'Information Architecture',
        ],
        agents: ['Research synthesis agent', 'Accessibility checker agent', 'Design system agent'],
    },
    {
        label: 'Product Designer',
        domain: 'product',
        skills: ['Visual Design', 'Design Systems', 'Prototyping', 'Motion Design', 'Accessibility', 'Figma'],
        agents: ['Component spec agent', 'Design QA agent', 'Handoff notes agent'],
    },
    {
        label: 'Content Strategist',
        domain: 'product',
        skills: [
            'Content Strategy',
            'Brand Strategy',
            'User Research',
            'Information Architecture',
            'Product Analytics',
        ],
        agents: ['Tone checker agent', 'Content gap agent', 'SEO audit agent'],
    },
    {
        label: 'UX Researcher',
        domain: 'product',
        skills: [
            'User Research',
            'Usability Testing',
            'A/B Testing',
            'Statistical Modeling',
            'Information Architecture',
            'Product Analytics',
        ],
        agents: ['Interview analysis agent', 'Theme extraction agent', 'Persona updater agent'],
    },
    {
        label: 'Design Lead',
        domain: 'product',
        skills: [
            'Design Systems',
            'Visual Design',
            'Figma',
            'Brand Strategy',
            'Prototyping',
            'Motion Design',
            'Accessibility',
        ],
        agents: ['Design review agent', 'Consistency audit agent', 'Portfolio scorer agent'],
    },
    // Sales & Marketing
    {
        label: 'Growth Marketer',
        domain: 'sales',
        skills: ['SEO', 'SEM', 'Content Marketing', 'Email Marketing', 'Lead Scoring', 'Account-Based Marketing'],
        agents: ['Campaign performance agent', 'A/B test analyzer agent', 'Attribution modeling agent'],
    },
    {
        label: 'Sales Manager',
        domain: 'sales',
        skills: ['Outbound Sales', 'CRM', 'Salesforce', 'Sales Enablement', 'Negotiation', 'Customer Success'],
        agents: ['Pipeline forecast agent', 'Deal coaching agent', 'RFP response agent'],
    },
    {
        label: 'Customer Success Lead',
        domain: 'sales',
        skills: ['Customer Success', 'Churn Analysis', 'CRM', 'HubSpot', 'Social Media', 'Inbound Marketing'],
        agents: ['Churn risk agent', 'NPS analyzer agent', 'Health score agent'],
    },
    {
        label: 'Marketing Director',
        domain: 'sales',
        skills: [
            'Content Marketing',
            'Brand Strategy',
            'SEO',
            'Social Media',
            'Account-Based Marketing',
            'Email Marketing',
        ],
        agents: ['Brand voice agent', 'Campaign brief agent', 'Competitive ad agent'],
    },
    {
        label: 'Revenue Operations Lead',
        domain: 'sales',
        skills: ['Salesforce', 'CRM', 'Lead Scoring', 'Sales Enablement', 'HubSpot', 'Churn Analysis', 'FP&A'],
        agents: ['Data hygiene agent', 'Territory optimizer agent', 'Commission calc agent'],
    },
    {
        label: 'Demand Generation Manager',
        domain: 'sales',
        skills: ['SEM', 'Content Marketing', 'Email Marketing', 'Inbound Marketing', 'Lead Scoring', 'A/B Testing'],
        agents: ['Channel mix agent', 'Lead scoring agent', 'Budget allocator agent'],
    },
    // Additional roles for denser graph
    {
        label: 'Technical Writer',
        domain: 'product',
        skills: [
            'Technical Writing',
            'Documentation',
            'API Documentation',
            'Markdown',
            'User Research',
            'Information Architecture',
        ],
        agents: ['Doc consistency agent', 'API doc generator agent', 'Glossary maintainer agent'],
    },
    {
        label: 'Solutions Architect',
        domain: 'tech',
        skills: [
            'Cloud Architecture',
            'AWS',
            'Microservices',
            'API Design',
            'System Design',
            'Kubernetes',
            'Terraform',
        ],
        agents: ['RFP response agent', 'Architecture diagram agent', 'Migration planner agent'],
    },
    {
        label: 'Integration Engineer',
        domain: 'tech',
        skills: ['API Design', 'REST', 'GraphQL', 'ETL Pipelines', 'Workflow Automation', 'SSO', 'OAuth'],
        agents: ['Integration tester agent', 'Error classifier agent', 'Schema validator agent'],
    },
    {
        label: 'BI Analyst',
        domain: 'data',
        skills: [
            'SQL',
            'Tableau',
            'Power BI',
            'Data Warehousing',
            'Dashboard Design',
            'ETL Pipelines',
            'Statistical Modeling',
        ],
        agents: ['Report scheduler agent', 'Anomaly alert agent', 'Metric definition agent'],
    },
    {
        label: 'Talent Sourcer',
        domain: 'people',
        skills: ['Recruiting', 'LinkedIn', 'Boolean Search', 'Employer Branding', 'Candidate Sourcing', 'CRM'],
        agents: ['Boolean builder agent', 'Candidate matching agent', 'Outreach sequence agent'],
    },
    {
        label: 'Compensation Partner',
        domain: 'people',
        skills: ['Compensation', 'Job Architecture', 'Pay Transparency', 'Equity', 'Benchmarking', 'HR Analytics'],
        agents: ['Job matching agent', 'Offer builder agent', 'Pay equity agent'],
    },
    {
        label: 'Change Manager',
        domain: 'strategy',
        skills: ['Change Management', 'Stakeholder Management', 'Communication', 'Training', 'Process Design', 'Agile'],
        agents: ['Impact assessor agent', 'Training plan agent', 'Resistance tracker agent'],
    },
    {
        label: 'M&A Integration Lead',
        domain: 'strategy',
        skills: ['M&A', 'Due Diligence', 'Integration Planning', 'Culture Design', 'Change Management', 'HR Strategy'],
        agents: ['DD checklist agent', 'Day-1 playbook agent', 'Synergy tracker agent'],
    },
    {
        label: 'Vendor Manager',
        domain: 'ops',
        skills: ['Vendor Management', 'Contract Management', 'Negotiation', 'SLA', 'Procurement', 'RFPs'],
        agents: ['Contract parser agent', 'SLA monitor agent', 'Vendor eval agent'],
    },
    {
        label: 'Service Designer',
        domain: 'product',
        skills: [
            'Service Design',
            'User Research',
            'Journey Mapping',
            'Workshop Facilitation',
            'Prototyping',
            'Design Thinking',
        ],
        agents: ['Journey mapper agent', 'Touchpoint analyzer agent', 'Blueprint generator agent'],
    },
    {
        label: 'Brand Manager',
        domain: 'sales',
        skills: [
            'Brand Strategy',
            'Content Marketing',
            'Social Media',
            'Creative Direction',
            'Marketing Analytics',
            'SEO',
        ],
        agents: ['Brand audit agent', 'Competitive tracker agent', 'Asset librarian agent'],
    },
    {
        label: 'Partner Manager',
        domain: 'sales',
        skills: ['Partner Relations', 'Channel Sales', 'Negotiation', 'CRM', 'Go-to-Market', 'Business Development'],
        agents: ['Partner scorecard agent', 'Enablement tracker agent', 'Co-marketing agent'],
    },
    {
        label: 'Credit Analyst',
        domain: 'finance',
        skills: [
            'Credit Analysis',
            'Financial Modeling',
            'Risk Management',
            'Regulatory Reporting',
            'IFRS',
            'Due Diligence',
        ],
        agents: ['Credit scoring agent', 'Financial statement agent', 'Covenant monitor agent'],
    },
    {
        label: 'Payroll Specialist',
        domain: 'people',
        skills: ['Payroll', 'Benefits Admin', 'HRIS', 'Tax Planning', 'Compliance', 'Workday'],
        agents: ['Pay run validator agent', 'Tax filing agent', 'Benefit enrollment agent'],
    },
    {
        label: 'Learning Experience Designer',
        domain: 'people',
        skills: ['L&D', 'Instructional Design', 'eLearning', 'Learning Technology', 'UX', 'Accessibility'],
        agents: ['Curriculum mapper agent', 'Assessment builder agent', 'Accessibility review agent'],
    },
];
